import { useEffect, type RefObject } from 'react';

/**
 * A section's contents rise into place as it scrolls into view.
 *
 * `Section` renders `data-reveal="auto"` on its container and calls this. Once
 * that section has hydrated, a section still below the fold is marked
 * `pending` (which the stylesheet hides) and revealed the first time it comes
 * into view: `shown` while the rise plays, `done` once it has. A section
 * already on screen — or above it, on a page opened at an anchor — is left
 * visible from the start.
 *
 * ---------------------------------------------------------------------------
 * Why each section arms itself
 * ---------------------------------------------------------------------------
 * The first version was one effect in the shell that swept the document. The
 * shell hydrates before a lazily-loaded route does, so the sweep rewrote
 * attributes React had not hydrated yet — a hydration mismatch — and when the
 * effect re-ran it left sections `pending` with nothing observing them. An
 * effect here runs after *this* section has hydrated, which is the only moment
 * it is safe to touch it. The observer behind it is shared.
 */
let observer: IntersectionObserver | null = null;

function sharedObserver() {
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer?.unobserve(entry.target);
        const element = entry.target;
        element.setAttribute('data-reveal', 'shown');
        window.setTimeout(() => element.setAttribute('data-reveal', 'done'), 1100);
      }
    },
    { rootMargin: '0px 0px -10% 0px' }
  );
  return observer;
}

export function useReveal(ref: RefObject<HTMLElement | null>, enabled: boolean) {
  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (element.getBoundingClientRect().top < window.innerHeight) return;

    element.setAttribute('data-reveal', 'pending');
    const shared = sharedObserver();
    shared.observe(element);

    return () => {
      shared.unobserve(element);
      // Never leave a section hidden behind an observer that has stopped.
      if (element.getAttribute('data-reveal') === 'pending') element.setAttribute('data-reveal', 'done');
    };
  }, [ref, enabled]);
}
